/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */
import { useEffect, useRef, useState } from 'react';
import './video-player.css';
const PLAY_ICON =
<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7L8 5Z" /></svg>;
const PAUSE_ICON =
<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>;
const RING_R = 17;
const RING_CIRC = 2 * Math.PI * RING_R;
export default function VideoPlayer({ src, poster, posterNode = null, className = '', posterFetchPriority = 'auto', showToggle = true }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [bufferedPct, setBufferedPct] = useState(0);
  const [hasDuration, setHasDuration] = useState(false);
  const readyRef = useRef(false);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    v.muted = true;
    function updateProgress() {
      if (readyRef.current) return;
      const dur = v.duration;
      if (!Number.isFinite(dur) || dur <= 0) return;
      setHasDuration(true);
      const buf = v.buffered;
      const end = buf.length ? buf.end(buf.length - 1) : 0;
      setBufferedPct(Math.min(100, Math.max(0, end / dur * 100)));
    }
    const onLoadedData = () => {readyRef.current = true;setReady(true);};
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onError = () => setLoadError(true);
    v.addEventListener('loadeddata', onLoadedData);
    v.addEventListener('loadedmetadata', updateProgress);
    v.addEventListener('progress', updateProgress);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('error', onError);
    return () => {
      v.removeEventListener('loadeddata', onLoadedData);
      v.removeEventListener('loadedmetadata', updateProgress);
      v.removeEventListener('progress', updateProgress);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('error', onError);
    };
  }, []);
  useEffect(() => {
    readyRef.current = false;
    setReady(false);
    setLoadError(false);
    setPlaying(true);
    setBufferedPct(0);
    setHasDuration(false);
    const v = videoRef.current;
    if (!v) return;
    v.src = src;
    v.currentTime = 0;
    v.play().catch(() => {});
  }, [src]);
  function toggle() {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});else
    v.pause();
  }
  const blockContextMenu = (e) => e.preventDefault();
  return (
    <div className={`vpl-frame ${className}`}>
      {!ready && posterNode}
      {!ready && !posterNode && poster && <img src={poster} alt="" className="vpl-poster" fetchPriority={posterFetchPriority} draggable={false} onContextMenu={blockContextMenu} />}
      {!ready && !loadError &&
      <svg className="vpl-progress-ring" viewBox="0 0 40 40" aria-hidden="true">
          <circle className="vpl-progress-track" cx="20" cy="20" r={RING_R} />
          <circle className="vpl-progress-fill" cx="20" cy="20" r={RING_R}
        style={{ strokeDasharray: RING_CIRC, strokeDashoffset: RING_CIRC * (1 - (hasDuration ? bufferedPct : 0) / 100) }} />
        </svg>
      }
      {loadError && <div className="vpl-error">ویدیو بارگذاری نشد.</div>}
      <video
        ref={videoRef}
        className="vpl-video"
        muted
        loop
        playsInline
        preload="metadata"
        disablePictureInPicture
        onClick={toggle}
        onContextMenu={blockContextMenu}
        style={{ opacity: ready ? 1 : 0 }} />
      {!playing && ready && !loadError &&
      <div className="vpl-pause-flash" aria-hidden="true">
        <div className="vpl-pause-flash-icon">{PAUSE_ICON}</div>
      </div>
      }
      {showToggle &&
      <button type="button" className="vpl-toggle" onClick={toggle} aria-label={playing ? 'توقف' : 'پخش'}>
          {playing ? PAUSE_ICON : PLAY_ICON}
        </button>
      }
    </div>);
}
